
///
/// EarthBrowser
///
/// Created by Matt Giger on 11/20/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type RadarMaterial } from "@/earthbrowser/dataset-distribution/nexrad/RadarMaterial"
import { type TextureSeries } from "@/earthbrowser/datasets/TextureSeries"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type matrix } from "@stovokor/math/matrix"
import { type Geometry } from "@stovokor/shader/Geometry"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type RadarAtlasFrame } from "./RadarMaterial"

export class RadarRenderer extends Renderer {
    private modelViewProjection = new UniformDynamic<matrix>()
	private textureSeries: TextureSeries<RadarAtlasFrame>

    constructor(context: StovokorContext, geometry: Geometry, material: RadarMaterial) {
        const source = new ShaderSourceDefinition("volumetric_radar")
        super(context, source, geometry, [material])
		this.textureSeries = material.textureSeries

		// Back-face culling stays on (the engine default). The ray is
		// clipped to the box analytically, so a back-face fragment would
		// march exactly the same ray as the front-face fragment over it:
		// drawing both ran every pixel twice and blended the volume over
		// itself. Nothing is lost at the edges because the front faces
		// cover the box's full silhouette. (With the camera inside the box
		// neither variant draws; see RADAR_RENDERING.md for the fix.)

		// Disable depth writing so transparent layers don't occlude each other.
		// Keep depth testing enabled so volume doesn't render behind opaque objects.
		this.drawState.depthWrite = false
    }

    protected stateUpdate(state: RenderState): void {
		// Update the texture series with the current frame to give us the latest textures
		this.textureSeries.updateState(state)

	    this.modelViewProjection.value = state.modelViewProjection
        this.materials.forEach(material => material.renderUpdate?.(state))
    }
}
